export interface HoverConnectingEdgeInput {
  id: string;
  sourceId: string;
  targetId: string;
}

export interface HoverConnectingEdgeOptions {
  hoveredNodeId: string | null;
  edges: readonly HoverConnectingEdgeInput[];
  expandedNodeIds: readonly string[];
  primaryNodeId?: string;
}

/**
 * The edge linking a hovered node back toward the graph's focus origin (the
 * most-recently-expanded node, else the primary). Used to reveal that edge's
 * label and relationship tooltip while the node is hovered.
 *
 * Returns null when the hovered node is the focus itself (a node has no
 * relationship to itself) or has no edge to any origin.
 */
export const resolveHoverConnectingEdgeId = ({
  hoveredNodeId,
  edges,
  expandedNodeIds,
  primaryNodeId,
}: HoverConnectingEdgeOptions): string | null => {
  if (!hoveredNodeId) {
    return null;
  }

  const focusId = expandedNodeIds.at(-1) ?? primaryNodeId ?? null;
  if (hoveredNodeId === focusId) {
    return null;
  }

  // Prefer the current focus, then the primary, then any earlier expansion, so
  // a node shared across subgraphs describes its tie to the active node first.
  const origins: string[] = [];
  for (const key of [focusId, primaryNodeId, ...expandedNodeIds]) {
    if (key && key !== hoveredNodeId && !origins.includes(key)) {
      origins.push(key);
    }
  }

  for (const origin of origins) {
    const match = edges.find(
      edge =>
        (edge.sourceId === hoveredNodeId && edge.targetId === origin) ||
        (edge.targetId === hoveredNodeId && edge.sourceId === origin)
    );
    if (match) {
      return match.id;
    }
  }

  return null;
};
